import { tokenStorage } from '../utils/auth';
import { getBackendUrl } from '../utils/https';
import type { NewsFeed, ReportRunStats, StoredNews } from '../types/news';

export type SSEEvent =
  | { type: 'progress'; step: number; totalSteps: number; message: string }
  | { type: 'content'; text: string }
  | { type: 'complete'; wikiPageId: string; title: string; stats?: ReportRunStats; feed?: NewsFeed }
  | { type: 'error'; message: string };

export type ReportLanguage = 'en' | 'fr';
export type ReportDepth = 'standard' | 'deep';

export interface ReportOptions {
  focus?: string;
  language?: ReportLanguage;
  depth?: ReportDepth;
}

export interface NewsOptions {
  focus?: string;
  language?: ReportLanguage;
  /** Months to search back over; 0 lifts the limit entirely */
  windowMonths?: number;
}

class IntelligenceReportService {
  private baseURL = `${getBackendUrl()}/api`;

  async getNews(stakeholderId: string): Promise<StoredNews | null> {
    const token = tokenStorage.get();
    const response = await fetch(`${this.baseURL}/intelligence-report/news/${stakeholderId}`, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!response.ok) return null;
    const data = await response.json();
    return data ?? null;
  }

  generateNews(entityId: string, signal?: AbortSignal, options: NewsOptions = {}): AsyncGenerator<SSEEvent> {
    const body: Record<string, string | number> = { entityId };
    if (options.focus) body.additionalContext = options.focus;
    if (options.language) body.language = options.language;
    if (options.windowMonths !== undefined) body.windowMonths = options.windowMonths;
    return this.stream('/intelligence-report/generate-news', body, signal);
  }

  generateReport(
    entityType: 'stakeholder' | 'organization',
    entityId: string,
    signal?: AbortSignal,
    options: ReportOptions = {},
  ): AsyncGenerator<SSEEvent> {
    const body: Record<string, string | number> = { entityType, entityId };
    if (options.focus) body.additionalContext = options.focus;
    if (options.language) body.language = options.language;
    if (options.depth) body.depth = options.depth;
    return this.stream('/intelligence-report/generate', body, signal);
  }

  /** POSTs and yields the server-sent events as they arrive */
  private async *stream(
    path: string,
    body: Record<string, string | number>,
    signal?: AbortSignal,
  ): AsyncGenerator<SSEEvent> {
    const token = tokenStorage.get();
    const response = await fetch(`${this.baseURL}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
      signal,
    });

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorBody = await response.json();
        errorMessage = errorBody?.error?.message || errorBody?.message || errorMessage;
      } catch {
        // Use default error message
      }
      throw new Error(errorMessage);
    }

    if (!response.body) {
      throw new Error('No response body received');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    const parse = (line: string): SSEEvent | null => {
      const trimmed = line.trim();
      if (!trimmed.startsWith('data: ')) return null;
      try {
        return JSON.parse(trimmed.slice(6)) as SSEEvent;
      } catch {
        return null; // malformed line
      }
    };

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const event = parse(line);
          if (event) yield event;
        }
      }

      if (buffer.trim()) {
        const event = parse(buffer);
        if (event) yield event;
      }
    } finally {
      reader.releaseLock();
    }
  }
}

export const intelligenceReportService = new IntelligenceReportService();
